import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Coins, Sparkles, MapPin, ChevronLeft, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * TestimonialsCarousel — landing social proof.
 *
 * Fetches real top-earners from `/hero/testimonials` and auto-scrolls a
 * horizontal marquee of user cards.  Each card shows a real picture, name,
 * bio (or fallback), and coins earned in the last 7 days.
 *
 * Falls back gracefully when no eligible users exist (renders nothing).
 */
export default function TestimonialsCarousel({ autoScroll = true, intervalMs = 3500, content = {} }) {
  const headline = (content && content.testimonials_headline) || "Pessoas reais ganhando com o Futuro PDV";
  const subtitle = (content && content.testimonials_subtitle) || "Prova social · últimos 7 dias";
  const [items, setItems] = useState([]);
  const [overflow, setOverflow] = useState(false);
  const scrollerRef = useRef(null);
  const cursorRef = useRef(0);

  useEffect(() => {
    axios.get(`${API}/hero/testimonials?limit=8`)
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => setItems([]));
  }, []);

  // Recompute overflow whenever items or viewport size change
  useEffect(() => {
    const check = () => {
      const el = scrollerRef.current;
      if (!el) return;
      setOverflow(el.scrollWidth > el.clientWidth + 4);
    };
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [items]);

  useEffect(() => {
    if (!autoScroll || !items.length || !overflow) return;
    const el = scrollerRef.current;
    if (!el) return;
    const iv = setInterval(() => {
      // Pause while the user is hovering the carousel or typing anywhere (e.g. the sign-up form)
      const active = document.activeElement;
      const typing = active && (active.tagName === "INPUT" || active.tagName === "TEXTAREA" || active.isContentEditable);
      if (typing || el.matches(":hover")) return;
      cursorRef.current = (cursorRef.current + 1) % items.length;
      const card = el.children[cursorRef.current];
      // Scroll ONLY the horizontal track — never scrollIntoView (it also scrolls the page vertically)
      if (card) el.scrollTo({ left: card.offsetLeft - el.offsetLeft, behavior: "smooth" });
    }, intervalMs);
    return () => clearInterval(iv);
  }, [items, autoScroll, intervalMs, overflow]);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const step = el.clientWidth * 0.7;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  if (!items.length) return null;

  return (
    <section
      data-testid="testimonials-carousel"
      className="relative mx-4 md:mx-6 mt-4"
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-emerald-600" data-testid="testimonials-subtitle">
            <Sparkles className="w-3.5 h-3.5" /> {subtitle}
          </div>
          <h3 className="text-lg md:text-xl font-bold tracking-tight" data-testid="testimonials-headline">
            {headline}
          </h3>
        </div>
        <div className={`hidden sm:flex items-center gap-2 ${overflow ? "" : "opacity-0 pointer-events-none"}`}>
          <button
            onClick={() => scrollBy(-1)}
            className="w-8 h-8 rounded-full border border-border bg-card hover:bg-muted flex items-center justify-center"
            data-testid="testimonials-prev"
            aria-label="Anterior"
            disabled={!overflow}
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={() => scrollBy(1)}
            className="w-8 h-8 rounded-full border border-border bg-card hover:bg-muted flex items-center justify-center"
            data-testid="testimonials-next"
            aria-label="Próximo"
            disabled={!overflow}
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div
        ref={scrollerRef}
        className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-3 -mx-1 px-1"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        <style>{`[data-testid="testimonials-carousel"] div::-webkit-scrollbar{display:none}`}</style>
        {items.map((t, i) => (
          <article
            key={i}
            data-testid={`testimonial-card-${i}`}
            className="snap-start shrink-0 w-72 md:w-80 rounded-2xl border border-border bg-card hover-lift p-4 flex items-start gap-3"
          >
            <div className="relative shrink-0">
              <img
                src={t.picture}
                alt={t.name}
                className="w-14 h-14 rounded-2xl object-cover border-2 border-yellow-300"
                onError={(e) => { e.currentTarget.src = "https://api.dicebear.com/7.x/avataaars/svg?seed=" + encodeURIComponent(t.name || "u"); }}
              />
              <span className="absolute -bottom-1.5 -right-1.5 inline-flex items-center gap-0.5 h-6 px-1.5 rounded-full bg-yellow-400 text-yellow-950 text-[10px] font-black shadow">
                <Coins className="w-3 h-3" /> +{Number(t.coins_7d || 0).toLocaleString("pt-BR")}
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-bold text-sm truncate" data-testid={`testimonial-name-${i}`}>{t.name}</div>
              {t.city && (
                <div className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5">
                  <MapPin className="w-3 h-3" /> {t.city}
                </div>
              )}
              <p className="text-xs text-muted-foreground line-clamp-2 mt-1">
                {t.bio || "Curto, compartilho e sigo ganhando com o Futuro PDV."}
              </p>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
